import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Coffee,
  KeyRound,
  Loader2,
  Smartphone,
  Store,
  UserPlus,
} from "lucide-react";
import { BrandMark } from "../components/BrandMark";
import { AuthSidePanel } from "../components/AuthSidePanel";
import { ApiRequestError, api, type Me } from "../lib/api";

type BusinessType = "cafe" | "kost";

type RegisterRequestResponse = {
  requestId: string;
  phone: string;
  expiresIn: number;
};

const initialForm = {
  name: "",
  username: "",
  phone: "",
  tenantName: "",
  businessType: "cafe" as BusinessType,
  password: "",
  confirmPassword: "",
};

export function RegisterPage({ onLogin }: { onLogin: (me: Me) => void }) {
  const [form, setForm] = useState(initialForm);
  const [step, setStep] = useState<"details" | "otp">("details");
  const [request, setRequest] = useState<RegisterRequestResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  function update<Key extends keyof typeof form>(
    key: Key,
    value: (typeof form)[Key],
  ) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  async function sendOtp(nextForm: typeof form) {
    const response = await api<RegisterRequestResponse>(
      "/api/auth/register/request",
      {
        method: "POST",
        body: JSON.stringify({
          username: nextForm.username.trim().toLowerCase(),
          name: nextForm.name.trim(),
          phone: nextForm.phone.trim(),
          password: nextForm.password,
          tenant_name: nextForm.tenantName.trim(),
          business_type: nextForm.businessType,
        }),
      },
    );
    setRequest(response);
    setStep("otp");
    setCode("");
    setCooldown(60);
  }

  async function handleDetails(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!/^[a-z0-9._-]{3,32}$/.test(form.username.trim().toLowerCase())) {
      setError(
        "Username 3-32 karakter: huruf kecil, angka, titik, underscore, atau dash.",
      );
      return;
    }
    if (form.password.length < 8) {
      setError("Password minimal 8 karakter.");
      return;
    }
    if (form.password !== form.confirmPassword) {
      setError("Konfirmasi password belum sama.");
      return;
    }

    setLoading(true);
    try {
      await sendOtp(form);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat terhubung ke server. Coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!/^\d{6}$/.test(code.trim())) {
      setError("Kode OTP harus 6 digit.");
      return;
    }

    setLoading(true);
    try {
      const me = await api<Me>("/api/auth/register/verify", {
        method: "POST",
        body: JSON.stringify({ phone: form.phone.trim(), code: code.trim() }),
      });
      onLogin(me);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat terhubung ke server. Coba lagi.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (cooldown > 0) return;
    setError(null);
    setLoading(true);
    try {
      await sendOtp(form);
    } catch (requestError) {
      setError(
        requestError instanceof ApiRequestError
          ? requestError.message
          : "Tidak dapat mengirim ulang kode.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <AuthSidePanel
        eyebrow="Mulai dari satu meja"
        title={
          <>
            Usaha siap.
            <br />
            Pesanan rapi.
            <br />
            <em>Tanpa ribet.</em>
          </>
        }
        lead="Daftarkan cafe atau kost Anda, verifikasi nomor WhatsApp, lalu langsung kelola menu, meja, dan pembayaran."
        features={[
          { icon: Coffee, label: "Cafe & kost" },
          { icon: Smartphone, label: "Verifikasi WhatsApp" },
          { icon: Store, label: "Multi-outlet" },
        ]}
        footer="Akun owner memakai username. Nomor WhatsApp dipakai untuk verifikasi dan reset password."
      />

      <main className="auth-main">
        <div className="auth-card auth-card--wide">
          <Link to="/" className="auth-card__brand auth-card__brand--link">
            <BrandMark size="sm" />
            <span>Cafe System Control</span>
          </Link>

          <Link to="/" className="auth-back">
            <ArrowLeft size={14} /> Kembali ke beranda
          </Link>

          <p className="eyebrow">
            {step === "details" ? "Pendaftaran owner" : "Verifikasi"}
          </p>
          <h2>
            {step === "details" ? "Buat akun usaha" : "Cek WhatsApp Anda"}
          </h2>
          <p className="auth-subtitle">
            {step === "details"
              ? "Isi data owner dan usaha. Setelah itu kami kirim kode OTP 6 digit ke WhatsApp."
              : `Kode dikirim ke ${request?.phone ?? form.phone}. Masukkan 6 digit untuk mengaktifkan akun.`}
          </p>

          {error ? (
            <div className="alert alert--danger" role="alert">
              <AlertCircle size={17} />
              <span>{error}</span>
            </div>
          ) : null}

          {step === "details" ? (
            <form className="auth-form" onSubmit={handleDetails}>
              <div className="form-grid">
                <label className="field field--full">
                  <span>Nama lengkap</span>
                  <input
                    className="input"
                    type="text"
                    autoComplete="name"
                    placeholder="Nama owner"
                    value={form.name}
                    onChange={(event) => update("name", event.target.value)}
                    minLength={2}
                    maxLength={120}
                    required
                  />
                </label>

                <label className="field">
                  <span>Username</span>
                  <input
                    className="input"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="mis. owner.cafe"
                    value={form.username}
                    onChange={(event) => update("username", event.target.value)}
                    minLength={3}
                    maxLength={32}
                    required
                  />
                </label>

                <label className="field">
                  <span>Nomor WhatsApp</span>
                  <input
                    className="input"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    placeholder="08xxxxxxxxxx"
                    value={form.phone}
                    onChange={(event) => update("phone", event.target.value)}
                    minLength={8}
                    maxLength={20}
                    required
                  />
                </label>

                <label className="field">
                  <span>Nama usaha</span>
                  <input
                    className="input"
                    type="text"
                    autoComplete="organization"
                    placeholder="Nama cafe atau kost"
                    value={form.tenantName}
                    onChange={(event) => update("tenantName", event.target.value)}
                    minLength={2}
                    maxLength={120}
                    required
                  />
                </label>

                <label className="field">
                  <span>Jenis usaha</span>
                  <select
                    className="select"
                    value={form.businessType}
                    onChange={(event) =>
                      update("businessType", event.target.value as BusinessType)
                    }
                  >
                    <option value="cafe">Cafe / restoran</option>
                    <option value="kost">Kost / penginapan</option>
                  </select>
                </label>

                <label className="field">
                  <span>Password</span>
                  <input
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Minimal 8 karakter"
                    value={form.password}
                    onChange={(event) => update("password", event.target.value)}
                    minLength={8}
                    maxLength={256}
                    required
                  />
                </label>

                <label className="field">
                  <span>Konfirmasi password</span>
                  <input
                    className="input"
                    type="password"
                    autoComplete="new-password"
                    placeholder="Ulangi password"
                    value={form.confirmPassword}
                    onChange={(event) =>
                      update("confirmPassword", event.target.value)
                    }
                    minLength={8}
                    maxLength={256}
                    required
                  />
                </label>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="spin" size={17} />
                    Mengirim kode...
                  </>
                ) : (
                  <>
                    Kirim kode WhatsApp
                    <ArrowRight size={17} />
                  </>
                )}
              </button>

              <p className="auth-footnote">
                Sudah punya akun? <Link className="auth-link" to="/login">Masuk di sini</Link>
              </p>
            </form>
          ) : (
            <form className="auth-form" onSubmit={handleVerify}>
              <label className="field">
                <span>Kode OTP 6 digit</span>
                <input
                  className="input otp-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]*"
                  placeholder="000000"
                  value={code}
                  onChange={(event) =>
                    setCode(event.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  maxLength={6}
                  required
                />
              </label>

              <button
                type="submit"
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="spin" size={17} />
                    Memverifikasi...
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={17} />
                    Aktifkan akun
                  </>
                )}
              </button>

              <div className="auth-links">
                <button
                  type="button"
                  className="btn btn-ghost btn--sm"
                  onClick={() => setStep("details")}
                  disabled={loading}
                >
                  <ArrowLeft size={15} />
                  Ubah data
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn--sm"
                  onClick={() => void handleResend()}
                  disabled={loading || cooldown > 0}
                >
                  {cooldown > 0 ? `Kirim ulang (${cooldown}s)` : "Kirim ulang"}
                </button>
              </div>

              <p className="auth-footnote">
                <KeyRound size={13} /> Kode berlaku 5 menit dan hanya bisa
                dipakai sekali.
              </p>
            </form>
          )}

          <p className="auth-footnote">
            <UserPlus size={13} /> Dengan mendaftar, Anda membuat workspace
            owner terpisah dari akun superadmin platform.
          </p>
        </div>
      </main>
    </div>
  );
}
